import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'utils/dayjs';
import { get, isEmpty } from 'lodash';

import { getLYSpitDateFor, compareDateByDay } from '../../helpers/dateHelper';
import {
    mergeChangeData,
    mergeLastYearData,
    calculateTotals,
    mergeStats,
    formatIntoCalendarViewFormat,
} from './parser';
import { FEATURES } from 'utils/permissions';
import { format, getToday, isPast } from 'utils/dayjsUtils';

import { calculateBookingDatesFromRange } from 'containers/widgets/BusinessChange/utils';

import session from 'containers/App/session';
import forecastModule from 'containers/App/data/endpoints/forecastByDay';
import businessChangeModule from 'containers/App/data/endpoints/businessChangeByDay';
import roomsRevModule from 'containers/App/data/endpoints/currentRoomsRevenueByDay';
import budgetByDayModule from 'containers/App/data/endpoints/budgetByDay';
import lySpitRoomsRevenueByDayModule from 'containers/App/data/endpoints/lySpitRoomsRevenueByDay';

import CalendarView from './calendarView';
import { Skeleton } from 'antd';

const MonthView = () => {
    const dateDisplayFormat = 'ddd DD MMM YYYY';

    const propertyId = useSelector(session.selectors.selectSelectedPropertyId);
    const currency = useSelector(session.selectors.selectCurrency);
    const accessibleFeatures = useSelector(session.selectors.selectAccessibleFeatures);
    const forecast = useSelector(forecastModule.selectors.selectState);
    const roomsRevenueByDay = useSelector(roomsRevModule.selectors.selectState);
    const budgetByDay = useSelector(budgetByDayModule.selectors.selectState);
    const lySpitRoomsRevenueByDay = useSelector(lySpitRoomsRevenueByDayModule.selectors.selectState);
    const businessChangeState = useSelector(businessChangeModule.selectors.selectState);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const selectedDate = get(location, 'state.selectedDate');
    const [period, setPeriod] = useState({
        startDate: selectedDate
            ? dayjs(selectedDate).startOf('month').format('YYYY-MM-DD')
            : dayjs().startOf('month').format('YYYY-MM-DD'),
        endDate: selectedDate
            ? dayjs(selectedDate).endOf('month').format('YYYY-MM-DD')
            : dayjs().endOf('month').format('YYYY-MM-DD'),
        details: {
            year: dayjs().year(),
            type: 'custom',
        },
    });
    const [statsByDay, setStatsByDay] = useState([]);
    const [totals, setTotals] = useState({});

    const getMonthBoundaries = () => {
        const startDate = dayjs(period.startDate);
        const endDate = dayjs(period.endDate);
        return {
            startDate,
            endDate,
            lySpitStartDate: startDate.clone().subtract(52, 'weeks'),
            lySpitEndDate: endDate.clone().subtract(52, 'weeks'),
        };
    };

    const getApiData = () => {
        dispatch(roomsRevModule.actions.resetState());
        dispatch(budgetByDayModule.actions.resetState());
        dispatch(lySpitRoomsRevenueByDayModule.actions.resetState());
        dispatch(businessChangeModule.actions.resetState());
        dispatch(forecastModule.actions.resetState());
        const { startDate, endDate, lySpitStartDate, lySpitEndDate } = getMonthBoundaries();
        const { minBookingDatetime, maxBookingDatetime } = calculateBookingDatesFromRange(1);
        const startFormatted = startDate.format('YYYY-MM-DD');
        const endFormatted = endDate.format('YYYY-MM-DD');
        dispatch(roomsRevModule.actions.fetch(propertyId, startFormatted, endFormatted));
        dispatch(budgetByDayModule.actions.fetch(propertyId, startFormatted, endFormatted));
        dispatch(
            lySpitRoomsRevenueByDayModule.actions.fetch(
                propertyId,
                lySpitStartDate.format('YYYY-MM-DD'),
                lySpitEndDate.format('YYYY-MM-DD'),
            ),
        );

        dispatch(
            businessChangeModule.actions.fetch(
                propertyId,
                startFormatted,
                endFormatted,
                minBookingDatetime,
                maxBookingDatetime,
            ),
        );
        if (!isPast(endDate)) {
            dispatch(
                forecastModule.actions.fetch(
                    propertyId,
                    isPast(startDate) ? getToday() : format(startDate),
                    format(endDate),
                ),
            );
        }
    };

    const parseData = () => {
        const parsingOptions = {
            dateFormat: 'YYYYMMDD',
            precision: 'day',
            getLySpitDate: getLYSpitDateFor,
        };
        const roomsRevenueData = roomsRevenueByDay.data;
        const budgetData = budgetByDay.data || [];
        const lySpitRoomsRevenue = lySpitRoomsRevenueByDay.data;

        const data = mergeStats(
            roomsRevenueData,
            budgetData,
            forecast.data || [],
        );
        const parsedData = formatIntoCalendarViewFormat(data, parsingOptions);
        const parsedLySpit = formatIntoCalendarViewFormat(lySpitRoomsRevenue, parsingOptions);
        const mergedData = mergeChangeData(
            mergeLastYearData(parsedData, parsedLySpit, parsingOptions),
            businessChangeState.data,
            parsingOptions,
        );
        const calculatedTotals = calculateTotals(mergedData);

        setStatsByDay(mergedData);
        setTotals(calculatedTotals);

    };

    const onChangePeriod = (periodDetails) => {
        if (periodDetails) {
            setPeriod(periodDetails);
        }
    };

    const onClickCard = (date) => {
        const dateSelected = date.format('YYYY-MM-DD');
        navigate('/app/daily', {
            state: {
                dayToView: date,
                selectedDate: dateSelected,
            },
        });
    };

    useEffect(() => {
        getApiData();
        return () => {
            // on unmount
            dispatch(roomsRevModule.actions.resetState());
            dispatch(budgetByDayModule.actions.resetState());
            dispatch(lySpitRoomsRevenueByDayModule.actions.resetState());
            dispatch(businessChangeModule.actions.resetState());
            dispatch(forecastModule.actions.resetState());
            setStatsByDay([]);
            setTotals({});
        };
    }, [propertyId, period]);

    const isLoading =
        roomsRevenueByDay.fetching ||
        forecast.fetching ||
        budgetByDay.fetching ||
        lySpitRoomsRevenueByDay.fetching ||
        businessChangeState.fetching ||
        !roomsRevenueByDay.fetched ||
        (!isPast(getMonthBoundaries().endDate) && !forecast.fetched) ||
        !budgetByDay.fetched ||
        !lySpitRoomsRevenueByDay.fetched ||
        !businessChangeState.fetched;

    useEffect(() => {
        if (!isLoading) {
            parseData();
        }
    }, [isLoading]);

    return (
        <>
            {isLoading && (
                <>
                    <Skeleton active />
                    <Skeleton active />
                    <Skeleton active />
                </>)
            }
            {!isLoading && !isEmpty(totals) && (
                <CalendarView
                    currency={currency}
                    period={period}
                    dateChanged={onChangePeriod}
                    totals={totals}
                    cards={statsByDay}
                    showForecast={accessibleFeatures.includes(FEATURES.revenueForecast)}
                    cardsProps={{
                        dateDisplayFormat,
                        cardClicked: onClickCard,
                        compareDates: compareDateByDay,
                        showChange: true,
                        showBudget: false,
                    }}
                />
            )}
        </>
    );
};

export default MonthView;
